import { Layout, Table, Typography, Col } from "antd";
import React, { forwardRef, useCallback, useImperativeHandle, useState } from "react";
import axios from "api/axios";
import { useDpatIndicatorDataSource } from "hooks/useDpatIndicatorDataSource";
import { getAttributeValue, getFileLinkIfExist } from "utils/utils";
import { renderDpatEvidenceLink, rowsHaveAllEvidenceLinks } from "utils/renderDpatEvidenceLink";
import DpatSubIndicatorSection from "../setup/DpatSubIndicatorSection";
import DpatIndicatorCmsIntro from "../setup/DpatIndicatorCmsIntro";
import MaxScoreDisplay from "../setup/MaxScoreDisplay";
import ActualScoreDisplay from "../setup/ActualScoreDisplay";
import IndicatorScoreSection from "../setup/IndicatorScoreSection";
import Comment from "components/Comments";

const CERTIFICATE_DATA_ELEMENT = "POdgz2rYh8H";
const SITE_PHOTO_DATA_ELEMENT = "ghbK2pf5qiV";
const REPORT_DATA_ELEMENT = "hM6AUNKRbKB";

function fileUrl(eventId, dataElementUid) {
  return `https://dddp.gov.gh/api/events/files?eventUid=${eventId}&dataElementUid=${dataElementUid}`;
}

function buildEvidenceLink(reports, dataElement, trackedEntity) {
  const eventId = getFileLinkIfExist(reports, dataElement, trackedEntity);
  return eventId ? fileUrl(eventId, dataElement) : "Not Uploaded";
}

const DumpingSite = forwardRef(({
  year,
  districtId,
  hideComment,
  savedAssessmentData,
  assessmentStartLoading,
  isAssessmentStarted,
}, ref) => {
  const [scorei, setScorei] = useState(0);
  const [scoreii, setScoreii] = useState(0);
  const [data, setData] = useState([]);
  const [siteList, setSiteList] = useState([]);
  const [maxScore, setMaxScore] = useState(5);

  const { Header, Content } = Layout;
  const { Title, Text } = Typography;

  useImperativeHandle(ref, () => ({
    getData: () => ({
      indicator: "SDI5",
      area: "Environmental Health, Sanitation and Climate Action",
      maxScore,
      data,
      siteList,
      scorei,
      scoreii
    }),
  }));

  const DPAT_SLUG = "sdi5.0-5.2";

  const loadFromDhis2 = useCallback(() => {
    axios
      .get(`/tracker/trackedEntities?orgUnit=${districtId}&program=Txcfc03kUCi`)
      .then(result => {
        if (result.data.instances.length > 0) {
          axios
            .get(`/tracker/events?program=Txcfc03kUCi&orgUnit=${districtId}&startDate=${year}-01-01&endDate=${year}-12-31`)
            .then(resp => {
              const sites = result.data.instances;
              const reports = resp.data.instances;
              const tempSites = [];
              const tempSiteList = [];

              sites.forEach((site, index) => {
                const currentReport = reports.find(rep => rep.trackedEntity === site.trackedEntity);
                let isFunctional = "NO";

                if (currentReport) {
                  currentReport.dataValues.forEach(rep => {
                    if (rep.dataElement === "mmPYFeO5XD5" && rep.value === "true") {
                      isFunctional = "YES";
                    }
                  })
                }
                const geoLocation = getAttributeValue("Site Coordinates", site);
                const tempDataSet = {
                  siteAvailability: getAttributeValue("DPAT | Is the site Engineered or Well-maintained dumping site?", site) === "true" ? "YES" : "NO",
                  isFunctional,
                  documentAvailable: "",
                  geoLocation,
                  ownership: getAttributeValue("Is there a co-ownership for the site", site) === "true" ? "Co-Ownership" : "No Co-Ownership"
                };

                const tempSiteDataSet = {
                  key: site.trackedEntity || index + 1,
                  no: index + 1,
                  name: getAttributeValue("Name", site),
                  geoLocation,
                  type: getAttributeValue("Dumping Site - Waste Type", site),
                  certificate: buildEvidenceLink(reports, CERTIFICATE_DATA_ELEMENT, site.trackedEntity),
                  sitePhoto: buildEvidenceLink(reports, SITE_PHOTO_DATA_ELEMENT, site.trackedEntity),
                  report: buildEvidenceLink(reports, REPORT_DATA_ELEMENT, site.trackedEntity),
                };

                tempSiteList.push(tempSiteDataSet);
                tempSites.push(tempDataSet);
              });

              setData(tempSites);
              setSiteList(tempSiteList);

              const siteEvidenceComplete = rowsHaveAllEvidenceLinks(
                tempSiteList,
                ["certificate", "sitePhoto", "report"],
              );

              if (tempSites.length > 0) {
                setScorei(2);
              }

              if (tempSiteList.length > 0 && siteEvidenceComplete) {
                setScoreii(3);
              } else {
                setScoreii(0);
              }
            })
            .catch(err => console.log(err))
        }
      })
      .catch(err => console.log(err))
  }, [districtId, year]);

  useDpatIndicatorDataSource({
    isAssessmentStarted,
    savedAssessmentData,
    assessmentStartLoading,
    loadFromDhis2,
    applySnapshot: (snap) => {
      if (snap.data) setData(snap.data);
      if (snap.siteList) setSiteList(snap.siteList);
      if (snap.scorei != null) setScorei(snap.scorei);
      if (snap.scoreii != null) setScoreii(snap.scoreii);
      if (snap.maxScore != null) setMaxScore(snap.maxScore);
    },
    deps: [year, districtId, loadFromDhis2],
  });

  const dumpingSiteColumn = [
    { title: "Well-maintained dumping site or Engineered Landfills available (Yes/No)", dataIndex: "siteAvailability", key: "siteAvailability" },
    { title: "Functional final disposal site available (Yes/No)", dataIndex: "isFunctional", key: "isFunctional" },
    { title: "Documents of ownership of disposal sites available", dataIndex: "documentAvailable", key: "documentAvailable" },
    { title: "Geo Location", dataIndex: "geoLocation", key: "geoLocation" },
    { title: "Evidence of Ownership / Collaboration Structure", dataIndex: "ownership", key: "ownership" }
  ];

  const dumpingSiteListColumn = [
    { title: "No.", dataIndex: "no", key: "no" },
    { title: "Name of Site", dataIndex: "name", key: "name" },
    { title: "Geo Location", dataIndex: "geoLocation", key: "geoLocation" },
    { title: "Solid / Liquid waste", dataIndex: "type", key: "type" },
    {
      title: "Certificate",
      dataIndex: "certificate",
      key: "certificate",
      render: (value) => renderDpatEvidenceLink(value, "View Certificate"),
    },
    {
      title: "Site Photo",
      dataIndex: "sitePhoto",
      key: "sitePhoto",
      render: (value) => renderDpatEvidenceLink(value, "View Photo"),
    },
    {
      title: "Report",
      dataIndex: "report",
      key: "report",
      render: (value) => renderDpatEvidenceLink(value, "View Report"),
    },
  ];

  return (
    <Comment
      data={data}
      year={year}
      districtId={districtId}
      tableCommentedId={`sdi5.0-5.2-${year}`}
      hideComment={hideComment}
    >
      {({ renderCommentFeedbackFooter }) => (
          <DpatSubIndicatorSection code={DPAT_SLUG}>
          <DpatIndicatorCmsIntro
            year={year}
            slug={DPAT_SLUG}
            mainTitleStyle={{ marginTop: "30px" }}
          />

          <IndicatorScoreSection>
          <MaxScoreDisplay score={maxScore} />
                    <ActualScoreDisplay slug={DPAT_SLUG} label="SDI 5.0-5.2i Actual Score:" score={scorei} />
          <ActualScoreDisplay slug={DPAT_SLUG} label="SDI 5.0-5.2ii Actual Score:" score={scoreii} />
          </IndicatorScoreSection>
          <Title level={4} style={{ marginTop: "20px" }}>I- Evidence of Disposal of Refuse</Title>
          <Table
            columns={dumpingSiteColumn}
            dataSource={data || []}
            pagination={false} bordered />

          <Title level={4} style={{ marginTop: "20px" }}>II- List of dumping /final disposal site</Title>
          <Table
            columns={dumpingSiteListColumn}
            dataSource={siteList}
            rowKey="key"
            pagination={false} bordered />
            {!hideComment && renderCommentFeedbackFooter()}
          </DpatSubIndicatorSection>
      )}
    </Comment>
  );
})

export default DumpingSite;